import { memo, useEffect, useMemo, useRef } from "react";
import { ChevronLeft, ChevronRight, Copy, Plus, Trash2 } from "lucide-react";
import BoardCanvas from "./BoardCanvas";

export const MAX_PAGES = 20;

// Small thumbnails only need the gallery images, not the sharp board previews.
const thumbAssets = (assets = {}) =>
  Object.fromEntries(Object.entries(assets).map(([key, asset]) => [key, { ...asset, url: asset.thumb || asset.url }]));

// Redrawn only when its own board changes, so dragging on one board does not
// redraw every preview.
const PageThumb = memo(function PageThumb({ entry, index, active, aspect, assets, onSelect }) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      aria-label={`Board ${index + 1}`}
      className={`mb-page-thumb${active ? " is-active" : ""}`}
      onClick={() => onSelect(index)}
      title={`Board ${index + 1}`}
    >
      <span className="mb-page-preview">
        <BoardCanvas board={{ aspect, background: entry.background, items: entry.items }} assets={assets} compact fit showPlaceholders={false} />
      </span>
      <span className="mb-page-number">{index + 1}</span>
    </button>
  );
});

// The boards of a mood board along the bottom, with "Add board" in the middle.
export default function PageStrip({ pages, page, aspect, assets, onSelect, onAdd, onDuplicate, onDelete, onMove }) {
  const stripRef = useRef(null);
  const small = useMemo(() => thumbAssets(assets), [assets]);
  const full = pages.length >= MAX_PAGES;

  useEffect(() => {
    stripRef.current?.querySelector(".is-active")?.scrollIntoView({ block: "nearest", inline: "nearest" });
  }, [page, pages.length]);

  return (
    <div className="mb-pages">
      <div className="mb-pages-strip" ref={stripRef} role="tablist" aria-label="Boards">
        {pages.map((entry, index) => (
          <PageThumb key={entry.id} entry={entry} index={index} active={index === page} aspect={aspect} assets={small} onSelect={onSelect} />
        ))}
        <button
          type="button"
          className="mb-page-add"
          onClick={onAdd}
          disabled={full}
          title={full ? `Up to ${MAX_PAGES} boards` : "Add a board after this one"}
        >
          <Plus size={18} strokeWidth={2} aria-hidden="true" />
          <span>Add board</span>
        </button>
      </div>
      <div className="mb-pages-actions" aria-label="This board">
        <span>
          Board {page + 1} of {pages.length}
        </span>
        <button type="button" className="mb-icon-button" onClick={() => onMove(-1)} disabled={page === 0} title="Move earlier" aria-label="Move board earlier">
          <ChevronLeft size={15} strokeWidth={1.8} />
        </button>
        <button type="button" className="mb-icon-button" onClick={() => onMove(1)} disabled={page === pages.length - 1} title="Move later" aria-label="Move board later">
          <ChevronRight size={15} strokeWidth={1.8} />
        </button>
        <button type="button" className="mb-icon-button" onClick={onDuplicate} disabled={full} title="Duplicate board" aria-label="Duplicate board">
          <Copy size={14} strokeWidth={1.8} />
        </button>
        <button type="button" className="mb-icon-button danger" onClick={onDelete} disabled={pages.length === 1} title="Delete board" aria-label="Delete board">
          <Trash2 size={14} strokeWidth={1.8} />
        </button>
      </div>
    </div>
  );
}
